import DOMPurify from 'dompurify'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import CloseSVG from '../../../../assets/svg/close.svg'
import { formValidation } from '../../../../utils/functions'
import Button from '../../../button'
import FormInput from '../../../form-input'
import { employees as employeeAPI } from '../../../../api/employees'
import AsyncSelect from 'react-select/async'
import {
  useAddEmployeeTraining,
  useUpdateEmployeeTraining,
} from '../../../../redux/employees/hook/training'
import { useDispatch } from 'react-redux'
import { setAlert } from '../../../../redux/components/components-slice'
import Modal from '../../modal'
import FormTextArea from '../../../form-textarea'
import FormFileInput from '../../../form-file-input'
import QuestionBlock from './QuestionBlock'
import FormSwitch from '../../../form-switch'

function AddTraining({
  isOpen,
  closeModal,
  trainingData,
  headerText,
  trainerChangeType,
  refetch,
}) {
  const {
    register,
    handleSubmit,
    formState: { isValid },
    errors,
  } = useForm({
    mode: 'onChange',
  })

  const defaultQuestion = () => ({
    id: trainingData?.questions
      ? -Math.floor(Math.random() * 10000)
      : -Math.floor(Math.random() * 10000),
    type: '',
    text: '',
    options: ['', '', '', ''],
    correctAnswers: [],
    shortAnswer: '',
  })

  const [questions, setQuestions] = useState([defaultQuestion()])
  const [updateModule, setUpdateModule] = useState(
    trainerChangeType === 'add' ? 1 : 0
  )
  const addQuestion = () => {
    setQuestions((prev) => [...prev, defaultQuestion()])
  }

  const deleteQuestion = (id) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id))
  }

  const updateQuestion = (id, updated) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...updated } : q))
    )
  }

  const dispatch = useDispatch()
  const [reminder, setReminder] = useState(true)

  const { mutateAsync: addTraining, isLoading: addTrainingloading } =
    useAddEmployeeTraining()

  const { mutateAsync: editTraining, isLoading: editTrainingloading } =
    useUpdateEmployeeTraining()

  const [employees, setEmployees] = useState(() => {
    if (trainingData?.assignments) {
      return trainingData.assignments.map((assignment) => ({
        label: `${assignment.employee.first_name} ${assignment.employee.last_name}`,
        value: assignment.employee.id,
      }))
    }
    return []
  })
  const [publish, setPubilsh] = useState(1)

  const loadOptions = (inputValue) =>
    new Promise((resolve) => {
      employeeAPI
        .getEmployees({
          limit: 10,
          statusFilter: 1,
          search: inputValue,
          page: 1,
        })
        .then((response) => {
          let data = []

          response.data.forEach((item) => {
            data.push({
              label: `${item.first_name} ${item.last_name} | ${item.mobile}`,
              value: item.id,
            })
          })

          resolve(data)
        })
        .catch((err) => {
          resolve([])
        })
    })

  const submitForm = async (data) => {
    const module_name = DOMPurify.sanitize(data?.module_name)
    const module_description = DOMPurify.sanitize(data?.module_description)
    const passing_grade = DOMPurify.sanitize(data?.passing_grade)
    const retry_limit = DOMPurify.sanitize(data?.retry_limit)
    const deadline = DOMPurify.sanitize(data?.deadline)
    const timer = DOMPurify.sanitize(data?.timer)
    const reminder = DOMPurify.sanitize(data?.reminder)

    const formData = new FormData()

    formData.append('module_name', module_name)
    formData.append('module_description', module_description)
    formData.append('passing_grade', passing_grade)
    formData.append('retry_limit', retry_limit)
    formData.append('deadline', deadline)
    formData.append('timer', timer)
    formData.append('reminder', reminder ? 1 : 0)
    formData.append('is_publish', publish)

    if (!employees || employees.length === 0) {
      dispatch(
        setAlert(true, 'warning', 'Please select at least one employee.')
      )
      return
    }

    // Employees
    if (employees.length > 0) {
      employees.forEach((emp, index) => {
        formData.append(`employee_ids[${index}]`, emp.value)
      })
    }

    // file
    const file = data?.file?.[0]

    if (file?.size > 10 * 1024 * 1024 && trainerChangeType === 'add') {
      dispatch(setAlert(true, 'warning', 'File size must not exceed 10MB.'))
      return
    }
    if (trainerChangeType === 'add') {
      if (!file) {
        dispatch(setAlert(true, 'warning', 'Please upload a module document.'))
        return
      }
      formData.append('module_doc', file)
      setUpdateModule(1)
    } else {
      if (file) {
        formData.append('module_doc', file)
        setUpdateModule(1)
      } else if (trainingData?.module_doc) {
        formData.append('module_doc', trainingData.module_doc)
        setUpdateModule(0)
      }
    }

    formData.append('update_module_doc', updateModule)

    const hasInvalidQuestion = questions.some((q) => {
      if (q.type === 'multiple') {
        return !q.correctAnswers || q.correctAnswers.length === 0
      }

      if (q.type === 'truefalse') {
        return (
          !Array.isArray(q.correctAnswers) ||
          q.correctAnswers.length !== 1 ||
          (q.correctAnswers[0] !== 0 && q.correctAnswers[0] !== 1)
        )
      }

      return false
    })

    if (hasInvalidQuestion) {
      dispatch(
        setAlert(
          true,
          'warning',
          'Please select an answer for all multiple choice and true/false questions.'
        )
      )
      return
    }

    // Questions
    questions.forEach((q, qIndex) => {
      formData.append(`questions[${qIndex}][id]`, q.id)

      formData.append(`questions[${qIndex}][question]`, q.text)

      let type
      if (q.type === 'multiple') type = 'multiple_choice'
      else if (q.type === 'truefalse') type = 'true_false'
      else type = 'short_answer'

      formData.append(`questions[${qIndex}][type]`, type)

      if (q.type === 'multiple' || q.type === 'truefalse') {
        const correctKey =
          q.type === 'truefalse'
            ? q.correctAnswers[0] === 0
              ? 'true'
              : 'false'
            : String.fromCharCode(65 + q.correctAnswers[0])
        formData.append(`questions[${qIndex}][correct_answer]`, correctKey)
      } else {
        formData.append(`questions[${qIndex}][correct_answer]`, q.shortAnswer)
      }

      if (q.type === 'multiple') {
        q.options.forEach((opt, oIndex) => {
          formData.append(
            `questions[${qIndex}][options][${oIndex}][key]`,
            String.fromCharCode(65 + oIndex)
          )
          formData.append(`questions[${qIndex}][options][${oIndex}][text]`, opt)
        })
      }
    })

    const parsedPassingGrade = parseInt(passing_grade)
    const parsedTimer = parseInt(timer)

    if (
      isNaN(parsedPassingGrade) ||
      parsedPassingGrade <= 0 ||
      parsedPassingGrade > questions.length
    ) {
      dispatch(
        setAlert(
          true,
          'warning',
          `Passing grade must be greater than 0 and not exceed ${questions.length}.`
        )
      )
      return
    }

    if (isNaN(parsedTimer) || parsedTimer < 0) {
      dispatch(setAlert(true, 'warning', 'Timer cannot be negative.'))
      return
    }

    try {
      if (trainerChangeType === 'add') {
        await addTraining(formData).then((response) => {
          closeModal()
        })
      } else {
        const editPayload = { id: trainingData?.id, data: formData }
        await editTraining(editPayload).then((response) => {
          closeModal()
          refetch()
        })
      }
    } catch (error) {}
  }

  useEffect(() => {
    if (trainingData?.questions) {
      const formattedQuestions = trainingData.questions.map((q) => ({
        id: q.id,
        type:
          q.type === 'multiple_choice'
            ? 'multiple'
            : q.type === 'true_false'
            ? 'truefalse'
            : 'short',
        text: q.question,
        shortAnswer: q.correct_answer || '',
        correctAnswers:
          q.type === 'multiple_choice'
            ? [
                q.options.findIndex(
                  (opt) => opt.option_key === q.correct_answer.toUpperCase()
                ),
              ]
            : q.type === 'true_false'
            ? [q.correct_answer === 'true' ? 0 : 1]
            : [],
        options:
          q.type === 'multiple_choice'
            ? q.options.map((opt) => opt.option_text)
            : ['', '', '', ''],
      }))
      setQuestions(formattedQuestions)
    }
  }, [trainingData])

  console.log(trainingData?.module_doc, 'trainingData')

  return (
    <>
      <Modal
        scrollable={true}
        className='!max-w-[600px] !rounded-none'
        isOpen={isOpen}
        centered={true}
      >
        <div className='flex flex-col overflow-auto'>
          <form className='form'>
            <div className='pl-8 pr-8 pt-4'>
              <div className='flex flex-row justify-between items-center'>
                <span className='header-4'>{headerText}</span>
                <div className='cursor-pointer' onClick={() => closeModal()}>
                  <img src={CloseSVG} alt='close' />
                </div>
              </div>
            </div>
            <hr className='divider mb-2' />
            <div className='flex flex-col gap-2 mx-8'>
              <h1 className='font-bold text-base'>About Module</h1>
              <div className='flex flex-row md:flex-col w-full justify-between'>
                <div className='w-full'>
                  <FormInput
                    label='Module Title'
                    name='module_name'
                    type='text'
                    placeholder={'Name your module'}
                    defaultValue={trainingData?.module_name}
                    error={errors.module_name}
                    errorMessage={
                      errors.module_name && errors.module_name.message
                    }
                    inputRef={register(formValidation('text', true))}
                  />
                </div>
              </div>
              <div className='flex flex-row md:flex-col w-full justify-between'>
                <div className='w-full'>
                  <FormTextArea
                    label='Module Description'
                    name='module_description'
                    type='text'
                    placeholder={'Describe your module'}
                    defaultValue={trainingData?.module_description}
                    error={errors.module_description}
                    errorMessage={
                      errors.module_description &&
                      errors.module_description.message
                    }
                    inputRef={register(formValidation('text', true))}
                  />
                </div>
              </div>
              <div className='flex flex-col w-full'>
                <div className='flex flex-col gap-1'>
                  <p className='text-sm text-[#5A5A5F]'>Select Document</p>
                  <p className='text-xs text-[#5A5A5F]'>
                    Upload a video or pdf
                  </p>
                </div>
                <FormFileInput
                  multiSource={true}
                  inputRef={register(formValidation('file', false))}
                  accept='any'
                  value={trainingData?.module_doc}
                />
              </div>
              <>
                <div className='border bg-highlight p-2 rounded-md'>
                  <label className='text-[13px] leading-[23px] font-normal text-color-gray mb-1'>
                    Select Multiple Employees
                  </label>

                  <AsyncSelect
                    isMulti
                    cacheOptions
                    defaultOptions
                    className='text-[13px]'
                    loadOptions={loadOptions}
                    onChange={(options) => setEmployees(options)}
                    value={employees}
                    classNamePrefix='mySelect'
                  />
                </div>
                <hr className='divider mt-3 mb-0' />
              </>
              <div className='flex flex-col gap-1'>
                <p className='font-medium'>Questions</p>
                <p className='text-xs text-neutral-text'>
                  Please select the type of question you’d love to ask below,
                  you can also click on the “add question button” to add more
                  questions.
                </p>
              </div>
              <div className='flex flex-col gap-4'>
                {questions.map((q, index) => (
                  <QuestionBlock
                    key={q.id}
                    index={index}
                    data={q}
                    onUpdate={updateQuestion}
                    onDelete={deleteQuestion}
                  />
                ))}
              </div>
              <div
                onClick={addQuestion}
                className='border border-dashed mt-2 p-2 text-sm font-medium rounded-[30px] cursor-pointer w-[210px]'
              >
                + Add Another Question
              </div>
              <div className='flex flex-col mt-4'>
                <div className='flex flex-col gap-3'>
                  <p className='font-medium'>Passing Criteria Settings</p>
                  <div className='flex flex-wrap gap-3 items-center'>
                    <div className='flex flex-col gap-1'>
                      <p className='text-xs text-color-gray'>Question Count</p>
                      <p className='text-sm bg-highlight rounded-md flex items-center justify-center p-2 w-[150px] border'>
                        {questions.length}
                      </p>
                    </div>

                    <div className='w-[150px]'>
                      <FormInput
                        label='Passing Grade'
                        name='passing_grade'
                        placeholder={'Enter Passing Grade'}
                        type='number'
                        defaultValue={trainingData?.passing_grade}
                        error={errors.passing_grade}
                        errorMessage={
                          errors.passing_grade && errors.passing_grade.message
                        }
                        inputRef={register(formValidation('number', true))}
                      />
                    </div>
                    <div className='w-[150px]'>
                      <FormInput
                        label='Retry Limit'
                        name='retry_limit'
                        placeholder={'Enter Retry Limit'}
                        type='number'
                        defaultValue={trainingData?.retry_limit}
                        error={errors.retry_limit}
                        errorMessage={
                          errors.retry_limit && errors.retry_limit.message
                        }
                        inputRef={register(formValidation('number', true))}
                      />
                    </div>
                    <div className='w-[150px]'>
                      <FormInput
                        label='Set Deadline'
                        name='deadline'
                        type='date'
                        defaultValue={trainingData?.deadline}
                        error={errors.deadline}
                        errorMessage={
                          errors.deadline && errors.deadline.message
                        }
                        inputRef={register(formValidation('date', true))}
                      />
                    </div>
                    <div className='w-[150px]'>
                      <FormInput
                        label='Set Timer (minutes)'
                        placeholder={'Set Timer'}
                        name='timer'
                        type='number'
                        defaultValue={trainingData?.timer}
                        error={errors.timer}
                        errorMessage={errors.timer && errors.timer.message}
                        inputRef={register(formValidation('number', true))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='flex flex-col'>
                <p className='font-medium'>Reminder</p>
                <div className='w-[300px]'>
                  <FormSwitch
                    label={'Send reminder 2 days before deadline'}
                    name={'reminder'}
                    checked={reminder}
                    onClick={() => {
                      setReminder(!reminder)
                    }}
                  />
                </div>
              </div>
              <div className='w-full flex items-center gap-5 mt-[20px] pb-4 '>
                <div className='w-full h-full'>
                  <Button
                    text={'Save as Draft'}
                    theme='secondary'
                    onClick={(e) => {
                      e.preventDefault()
                      setPubilsh(0)
                      if (publish === 0) {
                        handleSubmit(submitForm)()
                      }
                    }}
                    type='submit'
                    disabled={addTrainingloading || editTrainingloading}
                    loading={addTrainingloading || editTrainingloading}
                  />
                </div>
                <div className='w-full h-full'>
                  <Button
                    disabled={addTrainingloading || editTrainingloading}
                    text={'Publish'}
                    onClick={(e) => {
                      e.preventDefault()
                      setPubilsh(1)
                      if (publish === 1) {
                        handleSubmit(submitForm)()
                      }
                    }}
                    type='submit'
                    loading={addTrainingloading}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </Modal>
    </>
  )
}

export default AddTraining
